<template>
  <section class="nossas-bellas">
    <h2 class="section-title">{{ t.title }}</h2>
    <ClientOnly>
      <!-- Desktop: grid com preview em vídeo no mouseover -->
      <div v-if="!isMobile()" class="container">
        <div class="row align-items-center">
          <div v-for="model in models" class="col-lg-2" :key="model.id">
            <div class="card-bella pb-2">
              <!-- Capa: o link cobre só imagem+vídeo. Desktop (≥970) clica → navega.
                   Mobile/tablet (<970): 1º toque revela+play (não navega), 2º toque no vídeo navega. -->
              <NuxtLink
                :to="{ name: 'language-modelos-name-photo', params: { name: model.slug } }"
                custom
                v-slot="{ href, navigate }"
              >
                <a
                  :href="href"
                  class="cover-link"
                  :aria-label="model.title"
                  @click="onCoverClick($event, navigate, model.id, !!model.preview)"
                >
                  <div
                    class="img-box"
                    :class="{ 'is-playing': revealed.has(model.id) }"
                    @mouseenter="playVideo"
                    @mouseleave="pauseVideo"
                  >
                    <img
                      :src="`${model.cover}`"
                      :class="{ 'imagem-estatica': model.preview }"
                      :alt="model.imageAltText ? model.imageAltText : model.title"
                      width="227"
                    />
                    <video v-if="model.preview" class="gif" playsInline muted loop>
                      <source :src="model.preview" type="video/mp4" />
                    </video>
                  </div>
                </a>
              </NuxtLink>
              <!-- Nome: link separado, sempre navega -->
              <div class="card-body d-flex flex-column align-items-center">
                <NuxtLink
                  :to="{ name: 'language-modelos-name-photo', params: { name: model.slug } }"
                  class="name-link"
                >
                  <h5 class="card-title mb-3 mt-2">{{ model.title }}</h5>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
        <div class="row d-flex align-items-center justify-content-center">
          <NuxtLink
            :to="{ name: 'language-modelos' }"
            class="cta cta--desktop"
          >
            {{ t.cta }}
            <font-awesome :icon="['fas', 'arrow-right']" />
          </NuxtLink>
        </div>
      </div>

      <!-- Mobile: grid 3 colunas. 1º toque revela+play (não navega), 2º toque no vídeo navega. -->
      <div v-else class="container">
        <div class="row align-items-center">
          <div v-for="model in models" class="col-bella-mobile" :key="model.id">
            <div class="card-bella pb-3">
              <NuxtLink
                :to="{ name: 'language-modelos-name-photo', params: { name: model.slug } }"
                custom
                v-slot="{ href, navigate }"
              >
                <a
                  :href="href"
                  class="cover-link"
                  :aria-label="model.title"
                  @click="onCoverClick($event, navigate, model.id, !!model.preview)"
                >
                  <div
                    class="img-box"
                    :class="{ 'is-playing': revealed.has(model.id) }"
                  >
                    <img
                      :src="`${model.cover}`"
                      :class="{ 'imagem-estatica': model.preview }"
                      :alt="model.imageAltText ? model.imageAltText : model.title"
                    />
                    <video v-if="model.preview" class="gif" playsInline muted loop>
                      <source :src="model.preview" type="video/mp4" />
                    </video>
                  </div>
                </a>
              </NuxtLink>
              <!-- Nome: link separado, sempre navega -->
              <div class="card-body d-flex flex-column align-items-center">
                <NuxtLink
                  :to="{ name: 'language-modelos-name-photo', params: { name: model.slug } }"
                  class="name-link"
                >
                  <h5 class="card-title mb-0 mt-2">{{ model.title }}</h5>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
        <div class="row d-flex align-items-center justify-content-center">
          <NuxtLink
            :to="{ name: 'language-modelos' }"
            class="cta cta--mobile"
          >
            {{ t.cta }}
            <font-awesome :icon="['fas', 'arrow-right']" />
          </NuxtLink>
        </div>
      </div>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import { isMobile } from "@/shared/utils/mobile";
import api from "@/api";

interface Model {
  id: number;
  title: string;
  slug: string;
  cover: string;
  preview?: string;
  imageAltText?: string;
}

const route = useRoute();
const models = ref<Model[]>([]);

const DESKTOP_MIN = 970;
const revealed = reactive(new Set<number>());

const t = computed(() =>
  route.params.language === "pt-br"
    ? { title: "NOSSAS BELLAS", cta: "Veja mais" }
    : { title: "OUR BELLAS", cta: "See more" }
);

onMounted(() => {
  getModels();
});

async function getModels() {
  const limit = isMobile() ? 9 : 20;
  api
    .get(
      "v1/modelos/getAllModels?order=order&language=" +
        route.params.language +
        "&limit=" +
        limit
    )
    .then(({ data }) => {
      models.value = data.modelsArray?.data ?? [];
      revealed.clear();
    });
}

function coverVideo(e: Event): HTMLVideoElement | null {
  return (e.currentTarget as HTMLElement).querySelector(".gif") as HTMLVideoElement | null;
}

// hover só existe no desktop (≥970); o guard neutraliza mouseenter sintético no touch
function playVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.play().catch(() => {});
}
function pauseVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.pause();
}

function onCoverClick(
  event: MouseEvent,
  navigate: (e?: MouseEvent) => void,
  id: number,
  hasPreview: boolean
) {
  // desktop: clicar em qualquer parte navega (SPA). navigate() faz preventDefault
  // no clique normal e respeita ctrl/cmd/clique-do-meio (abre a URL real via href).
  if (window.innerWidth >= DESKTOP_MIN) return navigate(event);
  // sem vídeo, ou vídeo já revelado → navega
  if (!hasPreview || revealed.has(id)) return navigate(event);
  // 1º toque: pausa/reseta os outros vídeos do grid e revela só este (1 por vez)
  event.preventDefault();
  const target = event.currentTarget as HTMLElement;
  pauseSiblingVideos(target);
  revealed.clear();
  revealed.add(id);
  target.querySelector<HTMLVideoElement>(".gif")?.play().catch(() => {});
}

// pausa e reseta todos os vídeos do mesmo grid/carrossel (garante 1 vídeo por vez)
function pauseSiblingVideos(clicked: HTMLElement) {
  const container = clicked.closest(".row, .carousel");
  container?.querySelectorAll<HTMLVideoElement>(".gif").forEach((v) => {
    v.pause();
    if (v.currentTime) v.currentTime = 0;
  });
}
</script>

<style scoped lang="css">
.nossas-bellas {
  background-color: #fff;
  padding-top: 16px;
}

.section-title {
  text-align: center;
  color: #000000;
  font-family: "Bebas-Neue-Light";
  font-size: 64px;
  font-weight: 300;
  letter-spacing: 0;
  margin: 0 0 40px;
}

.card-bella {
  background-color: transparent;
}

.card-title {
  height: 19px;
  color: #666666;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  text-align: center;
}

.img-box {
  position: relative;
  width: 100%;
}

.img-box img {
  display: block;
  width: 100%;
  height: auto;
}

.col-lg-2 {
  width: 20%;
}

/* imagem estática em fluxo: define o retângulo (largura/altura) do card */
.imagem-estatica {
  position: relative;
  transition: opacity 0.3s;
}

/* vídeo sobreposto: cobre exatamente a imagem em qualquer breakpoint */
.gif {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
}

/* reveal por toque (<970): a classe só é adicionada quando innerWidth < 970,
   por isso a regra fica no base (cobre também 770–969) */
.is-playing .gif {
  opacity: 1;
}
.is-playing .imagem-estatica {
  opacity: 0;
}

.cover-link {
  display: block;
  text-decoration: none;
}
.name-link {
  text-decoration: none;
  color: inherit;
}

/* reveal por hover só no desktop (≥970), sem zoom */
@media screen and (min-width: 970px) {
  .img-box:hover .gif {
    opacity: 1;
  }
  .img-box:hover .imagem-estatica {
    opacity: 0;
  }
}

/* CTA */
.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: "Exo-2";
  font-size: 16px;
  font-weight: 400;
  border-radius: 30px;
  padding: 14px 40px;
  text-decoration: none;
  margin-top: 16px;
  width: 200px;
}

.cta--desktop {
  background-color: #b20000;
  border: 1px solid #b20000;
  color: #ffffff;
  margin-bottom: 70px;
}

.cta--desktop svg {
  color: #ffffff;
}

.cta--mobile {
  background-color: #000000;
  border: 1px solid #000000;
  color: #ffffff;
}

.cta--mobile svg {
  color: #b20000;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .nossas-bellas {
    background-color: #f0f0f0;
  }

  .section-title {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .col-bella-mobile {
    width: 33.3333%;
    padding-left: 6px;
    padding-right: 6px;
  }

  .img-box {
    height: auto;
  }

  .img-box img {
    width: 100%;
  }

  .card-title {
    font-size: 13px;
    line-height: 16px;
  }
}

/* === Tablet/iPad (770–1199px): escala a grade desktop para caber === */
@media screen and (min-width: 770px) and (max-width: 1199px) {
  .section-title {
    font-size: 44px;
    margin-bottom: 24px;
  }
  .img-box {
    height: auto;
  }
  .img-box img {
    width: 100%;
    height: auto;
  }
}
/* telas estreitas de tablet: 4 colunas em vez das 5 do desktop (evita espremer) */
@media screen and (min-width: 770px) and (max-width: 1024px) {
  .col-lg-2 {
    width: 25%;
  }
}
</style>
